import React from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';

import { ImportProgressChip } from '../import/ImportProgressChip';
import { useImportOnboarding } from '../../contexts/ImportOnboardingContext';
import { colors, radii, spacing, typography } from '../../core/theme/tokens';

type Props = {
  onImport: () => void;
  onShowAll?: () => void;
};

/**
 * Compact Spend import CTA — solid primary (not glass) so it reads as the
 * main action above charts. While an import runs in the background, swaps
 * the primary button for {@link ImportProgressChip}.
 */
export function SpendImportCta({ onImport, onShowAll }: Props) {
  const { busy, progress, isModalVisible } = useImportOnboarding();
  const importActive =
    !isModalVisible &&
    (busy || progress.phase === 'complete' || progress.phase === 'failed');

  return (
    <View style={styles.wrap}>
      {importActive ? (
        <ImportProgressChip onPress={onImport} />
      ) : (
        <Pressable
          onPress={onImport}
          accessibilityRole="button"
          accessibilityLabel="Import statements and screenshots"
          style={({ pressed }) => [styles.primaryBtn, pressed && styles.pressed]}
        >
          <Text style={styles.primaryLabel}>Import statements & screenshots</Text>
          <Text style={styles.primarySub}>
            Bank / cards for spend · Robinhood & Wealthsimple for investments
          </Text>
        </Pressable>
      )}

      {onShowAll ? (
        <Pressable
          onPress={onShowAll}
          accessibilityRole="button"
          accessibilityLabel="Show all transactions"
          hitSlop={8}
          style={({ pressed }) => [styles.secondaryLink, pressed && styles.pressed]}
        >
          <Text style={styles.secondaryText}>Show all transactions</Text>
        </Pressable>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  wrap: {
    gap: spacing.sm,
  },
  primaryBtn: {
    backgroundColor: colors.accent,
    borderRadius: radii.lg,
    paddingVertical: 14,
    paddingHorizontal: spacing.md,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 2,
    minHeight: 52,
  },
  primaryLabel: {
    ...typography.headline,
    color: colors.background,
    fontSize: 16,
    fontWeight: '700',
    textAlign: 'center',
  },
  primarySub: {
    ...typography.caption,
    color: 'rgba(11, 15, 20, 0.72)',
    fontSize: 12,
    textAlign: 'center',
  },
  secondaryLink: {
    alignSelf: 'center',
    paddingVertical: 4,
    paddingHorizontal: spacing.sm,
  },
  secondaryText: {
    ...typography.caption,
    color: colors.textMuted,
    fontWeight: '600',
  },
  pressed: {
    opacity: 0.88,
  },
});
